import { Theme } from "@amx/ux-twoui-theming";
import { Depths, IChoiceGroupOptionStyles, IStackStyles } from "@fluentui/react";
import { transition } from "../../../styles/constants";
import { TextAlignment } from "../../../styles/fonts";
import { highContrastMedia } from "../../../styles/media";
import { ChoiceGroupHeight } from "./props";

export const headerPadding = "0px 0px 12px 0px";
const buttonBorderRadius = "8px";

export const getTextVariant = (disabled?: boolean) => {
    return !disabled ? "bodyPrimaryText" : "bodyTertiaryText";
};

/** The styling for each of the buttons that is returned from the choice group's onRenderField property function */
export const getChoiceGroupButtonStyles = (
    theme: Theme,
    isMobile: boolean,
    height: ChoiceGroupHeight,
    textAlignment: TextAlignment,
    disabled?: boolean,
    selected?: boolean
) => {

    const unselectedBackgroundColor = disabled ? theme.palette.neutralLighter : theme.palette.neutralHigh;

    return {
        backgroundColor: selected ? theme.palette.themeLighterAlt : unselectedBackgroundColor,
        padding: isMobile ? "12px" : "24px",
        width: "100%",
        minHeight: isMobile ? "auto" : height,
        textAlign: textAlignment,
        borderRadius: buttonBorderRadius
    };
};

/**
 * Styling for the wrappers of each choice group option,
 * it is designed to wrap the components that use getChoiceGroupButtonStyles
 */
export const getChoiceGroupOptionStyles = (theme: Theme, isMobile: boolean, isSelected: boolean, disabled?: boolean, isSingleColumn?: boolean): IChoiceGroupOptionStyles => {
    const horizontalPadding = "8px";
    return {
        root: {
            width: isMobile || isSingleColumn ? "100%" : `calc(50% - ${horizontalPadding})`,
            paddingTop: 8
        },
        choiceFieldWrapper: {
            width: "100%",
            height: "100%",
            display: "flex",
            ...getChoiceFieldWrapperStyles(theme, isSelected, disabled)
        }
    };
};

/** General styling that should be applied to all options in ChoiceGroup button variants */
export const getChoiceFieldWrapperStyles = (theme: Theme, isSelected: boolean, disabled?: boolean) => {
    //Only return these styles if button is not disabled
    return !disabled ? {
        boxSizing: "content-box",
        border: "solid",
        borderWidth: isSelected ? "2px" : "1px",
        padding: isSelected ? "0px" : "1px",
        borderRadius: buttonBorderRadius,
        borderColor: isSelected ? theme.palette.themePrimary : theme.palette.neutralTertiary,
        [highContrastMedia.active]: {
            borderWidth: isSelected ? "4px" : "1px",
            padding: isSelected ? "0px" : "3px"
        },
        selectors: {
            ":focus-within": {
                outline: `3px solid`,
                outlineColor: theme.palette.neutralPrimary,
                borderWidth: "2px",
                padding: "0px",
                borderRadius: buttonBorderRadius
            },
            ":hover": {
                borderColor: !isSelected ? theme.palette.neutralPrimary : undefined,
                boxShadow: Depths.depth16,
                transitionDuration: transition.short
            },
            " input": {
                cursor: "pointer"
            }
        }
    } : {};
};

//Stylings for container that holds all the shimmer buttons
export const getChoiceGroupButtonShimmerContainerStyles = (): IStackStyles => {
    return {
        root: {
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            justifyContent: "space-between",
        },
    };
};

//Stylings for stack that hold shimmer containers
export const getShimmerContainerStyles = (): IStackStyles => {
    return {
        root: {
            width: "100%", //adding a width:100% here so that shimmers will respect margin/padding when their content is overflowing
            overflow: "hidden"
        }
    };
};
